import { ChangeEvent, FC, useEffect, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetAllStatesQuery } from "../../../../data/states/statesApi";
import { useGetEnumsQuery } from "../../../../../app/globalApi";
import { OptionType } from "../../../../../helpers/utils/StyleUtils";
import { handleRTKQuery } from "../../../../../helpers/utils/RtkQueryUtils";
import Modal from "../../../../../components/common/form/Modal";
import Form from "../../../../../components/common/form/Form";
import InputField from "../../../../../components/common/form/InputField";
import Button from "../../../../../components/common/form/Button";
import SelectField from "../../../../../components/common/form/SelectField";
import { createOptionListForSelectTag, datePicker } from "../../../../../helpers/utils/Utils";
import TextAreaField from "../../../../../components/common/form/TextAreaField";
import { useCreateAssociationFeeDetailApiMutation, useGetAssociationByIdQuery, useGetAssociationFeeByIdQuery, useUpdateAssociationFeeDetailApiMutation } from "../../associationApi";

interface props {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    associationFeeId?: string,
    associationId?: string,
    isReadOnly?: boolean;
}

const AssociationFeeModal: FC<props> = ({ show, setShow, associationFeeId, associationId, isReadOnly, }) => {

    const [feeData, setFeeData] = useState<any>({});

    const [updateAssociationFeeApi, updateAssociationFeeApiResponse] = useUpdateAssociationFeeDetailApiMutation();
    const [createAssociationFeeApi, createAssociationFeeApiResponse] = useCreateAssociationFeeDetailApiMutation();
    const getAssociationFeebyId = useGetAssociationFeeByIdQuery(associationFeeId ?? skipToken);
    const [phone, setPhone] = useState("");
    const [fax, setFax] = useState("");
    const { data: stateData } = useGetAllStatesQuery("");
    const { data: enums } = useGetEnumsQuery("");
    const [periodStartDate, setPeriodStartDate] = useState();
    const [periodEndDate, setPeriodEndDate] = useState();

    const [assessmentPeriodStart, setAssessmentPeriodStart] = useState(null);
    const [assessmentPeriodEnd, setAssessmentPeriodEnd] = useState(null);

    useEffect(() => {
        const obj = getAssociationFeebyId?.data;

        if (obj && enums) {
            const feeTypeOption = enums?.["FeeType"]?.find((type: any) => type.constant === obj.feeType);
            const frequencyOption = enums?.["Frequency"]?.find((freq: any) => freq.constant === obj.frequency);

            setFeeData({
                feeType: feeTypeOption ? { value: feeTypeOption.constant, label: feeTypeOption.value } : null,
                frequency: frequencyOption ? { value: frequencyOption.constant, label: frequencyOption.value } : null,
                feeAmount: obj.feeAmount || "",
                assessmentPeriodStart: obj.assessmentPeriodStart?.split("T")[0] || "",
                assessmentPeriodEnd: obj.assessmentPeriodEnd?.split("T")[0] || "",
                note: obj.note || ""
            });
            setPeriodEndDate(obj.assessmentPeriodEnd?.split("T")[0] || "");
            setPeriodStartDate(obj.assessmentPeriodStart?.split("T")[0] || "")
        }
    }, [getAssociationFeebyId?.data, enums]);




    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setFeeData({
            ...feeData,
            [name]: value
        })
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        const formattedData = {
            feeType: feeData?.feeType.value || "",
            feeAmount: parseFloat(feeData?.feeAmount) || 0,
            note: feeData?.note || "",
            frequency: feeData?.frequency.value || "",
            assessmentPeriodStart: periodStartDate,
            assessmentPeriodEnd: periodEndDate,
            associationId: associationId || "",
        };

        if (!associationFeeId) {
            await handleRTKQuery(
                async () => {
                    return await createAssociationFeeApi({
                        associationId: associationId,
                        requestBody: formattedData,
                        isSendLink: false
                    }).unwrap();
                },
                (isSuccess: boolean) => {
                    if (isSuccess) {
                        setFeeData({});
                        setShow(false);
                    }
                }
            );

        } else {
            await handleRTKQuery(
                async () => {
                    return await updateAssociationFeeApi({
                        associationId: associationFeeId,
                        requestBody: formattedData
                    }).unwrap();
                },
                (isSuccess: boolean) => {
                    if (isSuccess) {
                        setFeeData({});
                        setShow(false);
                    }
                }
            );
        }
    };



    return (
        <>
            <Modal
                title={associationFeeId ? "Update Association Fee" : "Add Association Fee"}
                show={show}
                setShow={setShow}
                className="w-2/5  bg-text-0 rounded-lg shadow-lg"
                fields={
                    <Form onSubmit={handleSubmit} fields={
                        <div className=" ">

                            <SelectField
                                placeholder='Select Type...'
                                value={feeData?.feeType || null}
                                options={createOptionListForSelectTag(enums?.["FeeType"], "value", "constant")}
                                onChange={(selectedOption) => {
                                    handleOnChange({ name: "feeType", value: selectedOption })
                                }}
                                label={"Type"}
                                required
                            />
                            <SelectField
                                placeholder='Select Frequency...'
                                value={feeData?.frequency || null}
                                options={createOptionListForSelectTag(enums?.["Frequency"], "value", "constant")}
                                onChange={(selectedOption) => {
                                    handleOnChange({ name: "frequency", value: selectedOption })
                                }}

                                label={"Frequency"}
                                required
                            />

                            <InputField
                                label="Amount"
                                name="feeAmount"
                                value={feeData?.feeAmount || ""}
                                type="text"
                                onChange={handleOnChange}
                                className="mb-2"
                                required
                            />
                            <div className=" col-span-12 md:grid md:grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    <label>Period Start</label>
                                </div>
                                <div className="col-span-9 mt-3">
                                    {datePicker(periodStartDate, setPeriodStartDate, false)}
                                </div>
                            </div>
                            <div className=" col-span-12 md:grid md:grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                    <label>Period End</label>
                                </div>
                                <div className="col-span-9 mt-3">
                                    {datePicker(periodEndDate, setPeriodEndDate, false)}
                                </div>
                            </div>

                            <TextAreaField
                                label="Note"
                                name="note"
                                value={feeData?.note || ""}
                                onChange={(e: any) => handleOnChange(e)}
                                className="mb-2 text-surface-2"
                            />

                            {/* Footer Buttons */}
                            <div className={'mt-4 border-t border-t-surface-5 flex justify-end pt-3 gap-3'}>


                                    <Button btnText="Cancel" isLoading={false} type="close" onClick={() => setShow(false)} />
                                    <Button
                                        btnText={associationFeeId ? "Update" : "Submit"}
                                        isLoading={associationFeeId ? updateAssociationFeeApiResponse.isLoading : createAssociationFeeApiResponse.isLoading}
                                        type="submit"
                                        isDisabled={false}
                                    />

                            </div>

                        </div >
                    } />

                }
            />

        </>
    )
}
export default AssociationFeeModal;